/**
 * Candidate .ifc files for the Differences-overview picker (WHA-3695 follow-up).
 *
 * Lists every processed IFC document for the building, RLS-scoped (a
 * subcontractor only sees their assigned projects), then narrows to the active
 * project when the building header is scoped to one — mirroring how the 3D
 * viewer's IfcModelStore applies `scopeProjectId` on top of a building-wide
 * fetch. Cached like the rest of the diff data (immutable set for a session).
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import type { FileCandidate, FileCandidateChain } from "@/components/compare/fileCandidates";
import { fetchIfcModelsForBuilding, type IfcModelRow } from "@/stores/ifcModel/repository";

/**
 * The candidate vocabulary and its chain/badge/ordering rules are shared with
 * the DWG Drawing Compare picker — they live in `@/components/compare/
 * fileCandidates` and are re-exported here so IFC call sites keep their names.
 */
export type IfcModelCandidate = FileCandidate;
export type IfcCandidateChain = FileCandidateChain;
export {
  candidateBadgeVersion,
  groupCandidatesIntoChains,
  orderCandidatePair,
} from "@/components/compare/fileCandidates";

interface UseIfcModelCandidatesResult {
  candidates: IfcModelCandidate[];
  /** version_group → how many candidates share it (drives "show badge only when
   * a file has multiple versions"). */
  versionGroupCounts: Map<string, number>;
  isLoading: boolean;
}

export function useIfcModelCandidates(
  buildingId: number | null,
  orgId: string | undefined,
  /** Active project scope from the building header, or null for whole-building. */
  projectId: number | null,
  enabled: boolean
): UseIfcModelCandidatesResult {
  const { data, isLoading } = useQuery<IfcModelRow[]>({
    queryKey: ["ifc-model-candidates", buildingId, orgId],
    queryFn: () => fetchIfcModelsForBuilding(buildingId as number, orgId as string),
    enabled: enabled && buildingId != null && !!orgId,
    staleTime: Infinity,
  });

  return useMemo(() => {
    const rows = data ?? [];
    // Whole-building scope shows everything; a scoped header narrows to that
    // project (documents with no project_id never match a specific scope).
    const scoped = projectId == null ? rows : rows.filter((r) => r.project_id === projectId);

    const versionGroupCounts = new Map<string, number>();
    for (const r of scoped) {
      if (r.version_group) {
        versionGroupCounts.set(r.version_group, (versionGroupCounts.get(r.version_group) ?? 0) + 1);
      }
    }

    const candidates: IfcModelCandidate[] = scoped.map((r) => ({
      docId: r.id,
      filename: r.filename,
      version: r.version,
      versionGroup: r.version_group,
      projectId: r.project_id,
      isLatestVersion: r.is_latest_version,
      createdAt: r.created_at,
      size: r.size,
      folderId: r.folder_id,
    }));

    return { candidates, versionGroupCounts, isLoading };
  }, [data, projectId, isLoading]);
}
